import React, { useMemo, useState } from "react";
import { C, font } from "../lib/styles.js";
import { DataContextTestProvider } from "../context/DataContext.jsx";
import { DocContextTestProvider } from "../context/DocContext.jsx";
import JobDetailStub from "../apps/jobs/views/JobDetailStub.jsx";
import { crewCardId, cardSessionId } from "../lib/crewCardRules.js";

// No-login fixture for the full Job Detail card (shell + Job Card form + Prep +
// Capture/Processing/Delivery + Sessions). JobDetailStub({jobId,user,onOpenJob})
// pulls all data from useData() and derives capability flags from
// jobAccess(user.role); user.role "admin" => every flag true + all live tabs.
// Mirrors E2EJobsFixture's DataContextTestProvider wiring.

const MOCK_JOB = {
  id: "e2e_jobdetail",
  clientName: "Acme Arts LLC",
  jobId: "acmart_260615",
  contactName: "Rina Patel",
  description: "Two-day full body capture — full rig.",
  status: "booked",
  captureDate: "2026-06-15",
  scheduledTime: "09:00",
  callTime: "08:30",
  wrapTime: "17:00",
  locationVenue: "Studio A",
  locationAddress: "123 Navy Yard",
  locationCity: "Brooklyn",
  locationState: "NY",
  locationZip: "11205",
  isStudio: false,
  industry: "fashion",
  clientCode: "acmart",
  project: "SS26 Lookbook",
  quoteDate: "2026-06-01",
  leadSource: "referral",
  revenue: 12450,
  invoiced: false,
  paid: false,
  linkedContacts: [{ contactId: "c1", role: "contact" }],
  notes: "Client provides lighting direction. Two looks.",
  deliverableRequirements: "50 hi-res textured OBJ + USDZ, color-corrected.",
  selectedEquipment: ["Big Rig", "Canon R5", "24mm Lens"],
  transport: "On Location",
  crew: ["Ivin", "Ethan"],
  prepNotes: "Full rig setup. Backup batteries.",
  lenses: ["24mm", "50mm"],
  additionalItems: ["Extra cards"],
  deliveryMethods: ["Google Drive"],
  deliveryDate: "2026-06-22",
  deliveredDate: "",
  modeler: "Post Vendor LLC",
  multiDay: false,
  sessions: [],
  quoteId: "q_e2e_1",
  jobDocs: [
    { id: "jd_e2e_1", title: "Pre-Scan Call — Acme", type: "conversation", content: "Discussed the two-day capture. Rina confirmed lighting.", date: "2026-06-08", participants: ["Rina Patel"], createdAt: "2026-06-08T15:00:00.000Z" },
  ],
  captureLog: [
    { id: "cap1", captureAttemptId: "cap1", subject: "Model 1", displayName: "Model 1", subjectType: "person", source: "qr_scan", status: "captured", equipment: { "Big Rig": true }, storage: "Drive A", operator: "Ivin", notes: "Full body", preview: true, hiResRequested: true, hiResProcessed: false, images: [] },
    { id: "cap2", captureAttemptId: "cap2", subject: "Model 2", displayName: "Model 2", subjectType: "person", source: "manual", status: "captured", equipment: { "Canon R5": true }, storage: "Drive B", operator: "Ethan", notes: "", preview: false, hiResRequested: false, images: [] },
  ],
};

// Linked quote so the Financials tab renders real numbers (total banner, line
// items, version history) instead of the no-quote state.
const MOCK_QUOTE = {
  id: "q_e2e_1",
  docType: "quote",
  quoteDate: "2026-06-01",
  clientName: "Acme Arts LLC",
  total: 12450,
  lineItems: [
    { description: "Full-body capture day (Big Rig)", qty: 2, rate: 4500 },
    { description: "Hi-res processing — 50 subjects", qty: 0, rate: 3450 },
  ],
  versions: [
    { schemaVersion: 2, timestamp: "2026-05-28T12:00:00.000Z", total: 11000, lineItems: [{ description: "Full-body capture day (Big Rig)", qty: 2, rate: 4000 }, { description: "Hi-res processing — 50 subjects", qty: 0, rate: 3000 }] },
  ],
  attachments: [],
};

// One generated doc so the Docs & Files documents list renders.
const MOCK_DOC = {
  id: "d_e2e_1",
  title: "Project Brief — Acme SS26",
  category: "project_brief",
  content: "<h1>Project Brief</h1><p>Two-day full body capture.</p>",
  jobId: "e2e_jobdetail",
  sharedWithClient: false,
  createdAt: "2026-06-05T10:00:00.000Z",
  updatedAt: "2026-06-05T10:00:00.000Z",
};

const STUDIO_SETTINGS = {
  equipment: {
    Scanners: [{ id: "big_rig", name: "Big Rig", note: "Full photogrammetry rig" }],
    Cameras: [{ id: "r5", name: "Canon R5", note: "Reference photos" }],
    Lenses: [{ id: "lens_24", name: "24mm Lens", note: "" }],
  },
  crewRoster: [
    { name: "Ivin", email: "", phone: "", receivesAllUpdates: true },
    { name: "Ethan", email: "", phone: "", receivesAllUpdates: true },
  ],
  storageOpts: ["Drive A", "Drive B"],
  transportOpts: ["On Location", "In Studio"],
};

// ?probono=1 seeds the job (and its quote) as pro bono (2026-09-11): the
// Financials tab shows the badge and Value provided, no invoiced/paid toggles.
const PRO_BONO_PARAM = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("probono");
const PRO_BONO_DISCOUNTS = [{ key: "qd_pb", name: "Pro bono", type: "%", value: 100, reason: "NYU class visit" }];
const proBonoQuote = (q) => (PRO_BONO_PARAM ? { ...q, billing: "pro_bono", total: 0, quoteDiscounts: PRO_BONO_DISCOUNTS } : q);

// ?multiday=1 seeds a 2-session multi-day job so the Session strip + editor are
// exercisable (mirrors how E2EJobsFixture uses ?protectedCaptureRow).
function initialJob() {
  const params = new URLSearchParams(window.location.search);
  if (PRO_BONO_PARAM) return { ...MOCK_JOB, billing: "pro_bono", grossRevenue: 0, invoiced: false, paid: false, quoteDiscounts: PRO_BONO_DISCOUNTS };
  if (!params.has("multiday")) return MOCK_JOB;
  const loc = { isStudio: false, locationVenue: "Studio A", locationAddress: "123 Navy Yard", locationCity: "Brooklyn", locationState: "NY", locationZip: "11205" };
  return {
    ...MOCK_JOB,
    multiDay: true,
    sessions: [
      { id: "s1", date: "2026-06-14", sessionType: "setup", callTime: "08:00", wrapTime: "12:00", ...loc },
      { id: "s2", date: "2026-06-15", sessionType: "capture", callTime: "08:30", wrapTime: "17:00", ...loc },
    ],
  };
}

// A crew card that has already been sent once, so the Cards pane's status rail
// renders the long card link, the recipient table and the Redeploy button (the
// widest things the 340px rail ever holds).
const MOCK_CREW_CARD = {
  id: crewCardId(MOCK_JOB.id, cardSessionId(MOCK_JOB)),
  jobId: MOCK_JOB.id,
  sessionId: cardSessionId(MOCK_JOB),
  assignments: [{ crewName: "Ivin", role: "Lead", note: "" }],
  lastDeploy: { token: "906377537ce116cc27c9345fb5af2da4", url: "https://nycsuite.io/card/906377537ce116cc27c9345fb5af2da4", version: 1, at: "2026-06-14T13:00:00.000Z", fingerprint: "stale", expiresAt: "2026-06-18T23:59:59.000Z", calendarError: "" },
  recipientStatus: [
    { name: "Ivin", email: "ivin@e2e.test", version: 1, sentAt: "2026-06-14T13:00:00.000Z", delivery: "sent", openedAt: "2026-06-14T13:23:00.000Z" },
    { name: "Ethan", email: "ethan@e2e.test", version: 1, sentAt: "2026-06-14T13:00:00.000Z", delivery: "sent", openedAt: "" },
  ],
};

// The sheet sections write field patches through saveCrewCard / saveClientCard
// and read the result back from crewCards / clientCards, so the fixture keeps
// both lists in state and upserts (the Sheets pane's controls then respond).
const upsertInto = (set) => async (card) => {
  set((prev) => (prev.some((c) => c.id === card.id) ? prev.map((c) => (c.id === card.id ? { ...c, ...card } : c)) : [...prev, card]));
  return true;
};

export default function E2EJobDetailFixture() {
  const [job, setJob] = useState(initialJob);
  const [crewCards, setCrewCards] = useState([MOCK_CREW_CARD]);
  const [clientCards, setClientCards] = useState([]);
  // ?role=crew renders the crew view (limited tabs + the crew Files tab).
  const role = new URLSearchParams(window.location.search).get("role") === "crew" ? "crew" : "admin";
  const user = { role, name: role === "crew" ? "E2E Crew" : "E2E Admin", email: `${role}@e2e.test` };

  const value = useMemo(() => ({
    jobs: [job],
    contacts: [{ id: "c1", name: "Rina Patel", email: "rina@acmearts.com" }],
    clients: [{ id: "cl1", name: "Acme Arts LLC" }],
    savedQuotes: [proBonoQuote(MOCK_QUOTE)],
    saveContact: async () => {},
    loadQuote: async () => {},
    amendQuote: async () => {},
    createChangeOrderQuote: () => {},
    studioSettings: STUDIO_SETTINGS,
    setStudioSettings: () => {},
    crewCards,
    clientCards,
    saveCrewCard: upsertInto(setCrewCards),
    saveClientCard: upsertInto(setClientCards),
    flushJobSave: async () => true,
    updateJob: (_id, patch) => setJob((j) => ({ ...j, ...patch })),
    updateJobStatus: (_id, status) => setJob((j) => ({ ...j, status })),
    toggleJobMilestone: () => {},
    deleteJob: () => {},
    unbookJob: () => {},
    unlinkQuote: () => {},
  }), [job, crewCards, clientCards]);

  return (
    <DataContextTestProvider value={value}>
      <DocContextTestProvider value={{ savedDocs: [MOCK_DOC], docsLoading: false, docsError: null, saveDoc: () => {}, deleteDoc: () => {}, contentBlocks: [] }}>
        <div style={{ minHeight: "100vh", background: C.bg, fontFamily: font, padding: 20 }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ fontSize: 11, color: C.amber, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 14 }}>
              Local test mode — Job Detail Fixture
            </div>
            <JobDetailStub jobId={job.id} user={user} onOpenJob={() => {}} />
          </div>
        </div>
      </DocContextTestProvider>
    </DataContextTestProvider>
  );
}
